import { storeBookingDetailsForV2 } from '@app/bookings/utils/bookingHelper'
import { SlotAvailabilityType } from 'app/Bookings/constants'
import { validateAndProceedBooking } from 'app/Bookings/utils'
import PrimaryBookButton from 'app/ProviderDetails/PrimaryBookButton'
import { getAvailabilityFilter } from 'app/ProviderDetails/StickyScheduleSection/AppointmentToggle/utils'
import {
  getFilterValueFromId,
  getPreviousRouterUrlQuery,
  queryFilters,
  setTrackProviderInformation,
} from 'app/ProviderDetails/utils'
import {
  EXTENDED_BOOKING_SPAN_DAYS,
  LabelAppointmentMapping,
  SLOT_LIST_LIMIT,
  SLOT_LOCATION_MAP,
} from 'app/ProviderSearch/constants'
import {
  callRudderEventForBookingModalAllAvailabilityShown,
  getSelectedPaymentType,
} from 'app/ProviderSearch/rudderEventUtils'
import {
  generateDatedSlots,
  generateDates,
  groupFilters,
  isSlotOutsideBookingWindow,
} from 'app/ProviderSearch/utils'
import ModalAvailabilityFilterPills from 'common/ModalAvailabilityFilterPills'
import PageLoader from 'common/PageLoader'
import Spinner from 'common/Spinner'
import TimeZonePicker from 'common/TimeZonePicker/V3'
import {
  DayOfWeek,
  INTAKE_BOOKING_WINDOW_HOURS,
  RudderstackIntegrations,
  sessionStorageKeys,
  TimeOfDay,
} from 'constants/common'
import {
  PRIMARY_BOOK_BUTTON_DEVICE,
  PRIMARY_BOOK_BUTTON_TRACKING,
} from 'constants/primaryBookButton'
import { profileDayOfWeekFilterOptions } from 'constants/profileDayOfWeekFilterOptions'
import { RudderEvent } from 'constants/rudderEvents'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { getProviderSlots } from 'context/ProviderBooking/ProviderSlots'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import getNextOpeningSlot from 'hooks/slots/getNextOpeningSlot'
import usePrimaryBookButtonSelection from 'hooks/slots/usePrimaryBookButtonSelection'
import useExtendedBookingTreatment from 'hooks/treatments/useExtendedBookingTreatment'
import usePrimaryBookButtonProfileV2 from 'hooks/treatments/usePrimaryBookButtonProfileV2'
import useAppSelector from 'hooks/useAppSelector'
import useThriveAuth from 'hooks/useThriveAuth'
import { useRouter } from 'next/router'
import { forwardRef, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'
import {
  cleanBookingInfo,
  startBooking,
  updateBookingDetails,
} from 'state/booking/actions'
import { initialBookingHelperState } from 'state/booking/constants'
import {
  countProviderSlots,
  filterDateSlotsByAppointmentMode,
  formatUserQuery,
  getCurrentZonnedTime,
  getParsedQuery,
  getSlotWithDayOfWeekAdded,
  getSlotWithTimeOfDayAdded,
  parseDayOfWeekFromQuery,
} from 'utils/common'
import logEvent from 'utils/logEvent'
import styles from './index.module.scss'
import SlotCard from './SlotCard'

type SlotGridBodyProps = {
  providerDetails: Provider
  userSelectedAvailType?: ProviderFilter[]
  showModal: boolean
  groupedFilters
  slotLocation?: string
  isProfileMobile?: boolean
}

const SlotGridBody = forwardRef<HTMLDivElement, SlotGridBodyProps>(
  (props, ref) => {
    const router = useRouter()
    const formattedQuery = getParsedQuery(router)
    const {
      providerDetails,
      groupedFilters,
      showModal,
      userSelectedAvailType,
      slotLocation,
      isProfileMobile = false,
    } = props
    const {
      isAuthenticated,
      loginWithRedirect,
      user,
      isSupportOrThirdPartyUser,
      isThirdPartyAgent,
    } = useThriveAuth()
    const dispatch = useDispatch()
    const { query } = router

    const { id, practice } = providerDetails
    const { timeZone, format } = useContext(TimeZoneContext)

    const { dbUser, userEmail, b2bUserDetails } = useAppSelector(
      (state) => state.bookingHelper
    )

    const { bookingAttribution, salesforceDetails } = useAppSelector(
      (state) => state.global
    )

    const providerList = useMemo(() => {
      if (providerDetails) {
        return [providerDetails]
      }

      return []
    }, [providerDetails?.id])

    const startSlotDate = useMemo(() => getCurrentZonnedTime(timeZone), [
      timeZone,
    ])
    const {
      insuranceId,
      selfPay,
      availabilityType: availabilityTypeId,
      userId,
      patients,
      counselingType,
    } = formatUserQuery(query)

    const { availabilityLabels, availabilityFilters } = getAvailabilityFilter(
      availabilityTypeId || [],
      providerDetails
    )
    const visitTypeOptions = availabilityFilters ?? []
    const { enabled: extendedBookingEnabled } = useExtendedBookingTreatment()

    // Extended booking caps the "view all" window at the env-driven horizon.
    const slotWindowLimit = extendedBookingEnabled
      ? EXTENDED_BOOKING_SPAN_DAYS
      : SLOT_LIST_LIMIT

    const bookingWindowHours = INTAKE_BOOKING_WINDOW_HOURS
    const {
      providerSlots,
      nextAvailabilityForProviders,
      loading: slotsLoading,
    } = getProviderSlots({
      startSlotDate: startSlotDate,
      slotListLimit: slotWindowLimit,
      providerList: providerList,
      practiceId: practice.id,
      timeZone,
      slotsType: '',
      skipTimeOfDayFilter: true,
      skipDayOfWeekFilter: true,
      extendedBookingEnabled,
      bookingWindowHours,
    })

    const initialVisitTypeIds =
      userSelectedAvailType?.length === 1 ? [userSelectedAvailType[0].id] : []
    const [visitTypeSelectedIds, setVisitTypeSelectedIds] = useState<string[]>(
      initialVisitTypeIds
    )
    const [timeOfDay, setTimeOfDay] = useState<TimeOfDay[]>(
      formattedQuery.timeOfDay ?? []
    )
    // Forward-only URL persistence: seed once from `?dayOfWeek[]=...` on mount.
    // Toggling inside the modal updates local state only — it does NOT push
    // back to the URL, matching the RFC's locked "forward-only" decision and
    // mirroring how `timeOfDay` already behaves a few lines above.
    const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek[]>(() =>
      parseDayOfWeekFromQuery(query)
    )

    // Derive the single-select appointment mode from the (multi-select) visit
    // type pill: exactly 1 selected → that mode; 0 or >1 → show both
    // (undefined). When the provider only has one availability label, lock to
    // it regardless of pill selection.
    const appointmentMode: SlotAvailabilityType | undefined = (() => {
      if (availabilityLabels.length === 1) {
        return availabilityLabels[0] as SlotAvailabilityType
      }

      if (visitTypeSelectedIds.length !== 1) {
        return undefined
      }

      const selected = visitTypeOptions.find(
        (o) => o.id === visitTypeSelectedIds[0]
      )

      if (selected?.value && LabelAppointmentMapping[selected.value]) {
        return LabelAppointmentMapping[selected.value] as SlotAvailabilityType
      }

      return undefined
    })()

    const dateSlots: DateSlot = providerSlots[id] || {}
    const { globalGroupedFilters } = useAppSelector((state) => state.global)
    const _counselingTypeFilters =
      globalGroupedFilters[ProviderFilterTypes.COUNSELING_TYPE]

    const timeOfDayFilters = globalGroupedFilters[
      ProviderFilterTypes.TIME_OF_DAY
    ]?.length
      ? globalGroupedFilters[ProviderFilterTypes.TIME_OF_DAY]
      : groupFilters(providerDetails.filters)[ProviderFilterTypes.TIME_OF_DAY]

    const dayOfWeekFilters = globalGroupedFilters[
      ProviderFilterTypes.DAY_OF_WEEK
    ]?.length
      ? globalGroupedFilters[ProviderFilterTypes.DAY_OF_WEEK]
      : groupFilters(providerDetails.filters)[ProviderFilterTypes.DAY_OF_WEEK]
          ?.length
      ? groupFilters(providerDetails.filters)[ProviderFilterTypes.DAY_OF_WEEK]
      : profileDayOfWeekFilterOptions

    const filteredDateSlotsByAppointmentMode: DateSlot = filterDateSlotsByAppointmentMode(
      dateSlots || {},
      appointmentMode
    )

    const {
      isEnabled: isPrimaryBookButtonV2Enabled,
    } = usePrimaryBookButtonProfileV2()
    // Scoped to this modal's mobile profile treatment — desktop's "view all
    // availability" use of this same modal is out of scope for THRIV-14185.
    const isPrimaryBookButtonMobileEnabled =
      isPrimaryBookButtonV2Enabled && isProfileMobile

    const nextOpeningSlot = isPrimaryBookButtonMobileEnabled
      ? getNextOpeningSlot(
          filteredDateSlotsByAppointmentMode,
          bookingWindowHours,
          nextAvailabilityForProviders,
          id
        )
      : null

    const dates: Date[] = generateDates(startSlotDate, slotWindowLimit)

    const generatedDatedSlots = generateDatedSlots(
      dates,
      filteredDateSlotsByAppointmentMode,
      (slot) => {
        if (!isSlotOutsideBookingWindow(slot?.start, bookingWindowHours)) {
          return null
        }

        let decoratedSlot: ProviderSlot | null = slot

        if (decoratedSlot && timeOfDay.length > 0) {
          decoratedSlot = getSlotWithTimeOfDayAdded(
            decoratedSlot,
            timeOfDay,
            timeZone
          )
        }

        if (decoratedSlot && dayOfWeek.length > 0) {
          decoratedSlot = getSlotWithDayOfWeekAdded(
            decoratedSlot,
            dayOfWeek,
            timeZone
          )
        }

        return decoratedSlot
      }
    )

    const providerSlotsCount = countProviderSlots(generatedDatedSlots)

    const userSelectedFilters = getFilterValueFromId(groupedFilters, query)
    const counselingTypes = groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]

    const flattenedCounselingTypeId =
      typeof formattedQuery?.counselingType === 'string'
        ? formattedQuery?.counselingType
        : formattedQuery?.counselingType?.[0]

    const counselingTypeValue = counselingTypes.find((type) => {
      return type.id === flattenedCounselingTypeId
    })

    const trackSlotSelected = (
      selectedSlot: SelectedSlot,
      slotType: 'primary' | 'normal'
    ) => {
      logEvent(
        RudderEvent.PROVIDER_SLOT_SELECTED,
        {
          count_provider_slots_shown: providerSlotsCount,
          slot_timestamp: selectedSlot.slot?.start,
          payment_type_filter: getSelectedPaymentType(
            getPreviousRouterUrlQuery()
          ),
          ...queryFilters(providerDetails, getPreviousRouterUrlQuery()),
          ...setTrackProviderInformation(providerDetails),
          account_id: user?.id,
          therapy_type: counselingTypeValue?.value || null,
          slot_type: slotType,
          ...(slotLocation && {
            slot_location:
              showModal === true && providerSlotsCount > 0
                ? SLOT_LOCATION_MAP.availability_modal
                : slotLocation,
          }),
          ...(isPrimaryBookButtonMobileEnabled && {
            surface: PRIMARY_BOOK_BUTTON_TRACKING.SURFACE,
            device: PRIMARY_BOOK_BUTTON_DEVICE.MOBILE,
            variant: PRIMARY_BOOK_BUTTON_TRACKING.VARIANT_LABEL,
          }),
        },
        {
          [RudderstackIntegrations.GOOGLE_ADS]: true,
        }
      )
    }

    const bookAppointment = (selectedSlot: SelectedSlot) => {
      let selectedSlotAppointmentType: SlotAvailabilityType = null

      if (selectedSlot.slot.availabilityType.length === 1) {
        selectedSlotAppointmentType =
          selectedSlot.slot.availabilityType[0] === SlotAvailabilityType.ONLINE
            ? SlotAvailabilityType.ONLINE
            : SlotAvailabilityType.INPERSON
      } else {
        selectedSlotAppointmentType = SlotAvailabilityType.ONLINE //default to video visit if none selected/ both selected
      }

      // ============================================================
      // FIX: Validate if user's selected filter (appointmentMode) matches the slot's availability
      // This handles waterfall bucket cases where availabilityType filter is removed
      // ============================================================
      let validatedAppointmentMode = appointmentMode

      if (appointmentMode) {
        // Check if the slot actually supports the user's selected appointment mode
        const slotSupportsSelectedMode = selectedSlot.slot.availabilityType.some(
          (type) => type === appointmentMode
        )

        // If slot doesn't support the user's filter, it's from a waterfall bucket
        // Use the slot's actual availability type instead
        if (!slotSupportsSelectedMode) {
          validatedAppointmentMode = selectedSlotAppointmentType
        }
      }

      const availTypeToAddInRouterUrl = !validatedAppointmentMode
        ? selectedSlotAppointmentType
        : validatedAppointmentMode

      const mappedAvailability = Object?.keys(LabelAppointmentMapping)?.find(
        (availabilityObject) => {
          if (
            LabelAppointmentMapping[availabilityObject] ===
            availTypeToAddInRouterUrl
          ) {
            return availabilityObject
          }
        }
      )

      const matchedAvailabilityFilter = groupedFilters[
        ProviderFilterTypes.AVAILABILITY_TYPE
      ]?.find((filter) => {
        if (filter?.value === mappedAvailability) {
          return filter
        }

        return
      })
      dispatch(cleanBookingInfo(isThirdPartyAgent))
      dispatch(
        startBooking({
          ...initialBookingHelperState,
          ...(!!b2bUserDetails && { b2bUserDetails }),
          selectedSlot,
          providerDetails,
          filters: {
            slotAvailabilityTypes: selectedSlot.slot.availabilityType,
          },
          ...(!!dbUser && { dbUser, userEmail: dbUser.email }),
          ...(!!userEmail && { userEmail }), //userEmail will be available if redirecting from Support dashboard.
        })
      )
      dispatch(
        updateBookingDetails({
          providerId: providerDetails.id,
          ...(formattedQuery?.counselingType && {
            counselingTypeId: flattenedCounselingTypeId,
          }),
          counselingType: counselingTypeValue ? counselingTypeValue.value : '',
          // Use validatedAppointmentMode for V1 booking flow
          ...(!validatedAppointmentMode
            ? { appointmentMode: selectedSlotAppointmentType }
            : { appointmentMode: validatedAppointmentMode }),
        })
      )

      storeBookingDetailsForV2({
        selectedSlot,
        providerDetails,
        appointmentModeId: matchedAvailabilityFilter?.id,
        counselingTypeId: flattenedCounselingTypeId,
        bookingAttribution,
        salesforceDetails,
      })

      const queryObject = {
        ...(counselingType && {
          counselingType: counselingType[0],
        }),
        ...(insuranceId && { insuranceId }),
        ...(selfPay && { selfPay }),
        ...(userId && { userId }),
        ...(patients && { patients }),
        ...(matchedAvailabilityFilter && {
          availabilityType: matchedAvailabilityFilter.id,
        }),
        slotId: selectedSlot.slot.id,
      }

      validateAndProceedBooking(
        isAuthenticated,
        loginWithRedirect,
        router,
        queryObject
      )
    }

    const {
      effectiveSlot: primaryBookButtonEffectiveSlot,
      isSlotSelected: isPrimaryBookButtonSlotSelected,
      isConfirming: isPrimaryBookButtonConfirming,
      onSlotClick: handlePrimaryBookButtonSlotClick,
      onConfirm: handlePrimaryBookButtonConfirm,
      resetSelectedSlot: resetPrimaryBookButtonSelectedSlot,
    } = usePrimaryBookButtonSelection({
      isEnabled: isPrimaryBookButtonMobileEnabled,
      nextOpeningSlot,
      providerDetails,
      // fc-058: dedicated click event for the primary book button, separate
      // from the "Provider Slot Selected" booking event fired inside
      // bookAppointment below.
      onBook: (selectedSlot, slotType) => {
        logEvent(
          RudderEvent.PROVIDER_PRIMARY_BOOK_BUTTON_CLICKED,
          {
            ...setTrackProviderInformation(providerDetails),
            account_id: user?.id,
            surface: PRIMARY_BOOK_BUTTON_TRACKING.SURFACE,
            device: PRIMARY_BOOK_BUTTON_DEVICE.MOBILE,
            variant: PRIMARY_BOOK_BUTTON_TRACKING.VARIANT_LABEL,
          },
          {
            [RudderstackIntegrations.GOOGLE_ADS]: true,
          }
        )

        // For a manually picked slot, Provider Slot Selected already fired on
        // slot click (trackSlotSelected below). The express path (confirming
        // the pre-loaded slot without picking one) never goes through that
        // click, so fire it here to avoid missing/duplicating the event.
        if (slotType === 'primary') {
          trackSlotSelected(selectedSlot, 'primary')
        }

        bookAppointment(selectedSlot)
      },
    })

    const handleVisitTypeSelectedIdsChange = (ids: string[]) => {
      setVisitTypeSelectedIds(ids)
      // A slot selected under the previous visit type filter may not be
      // valid for the new one, so require the user to reconfirm a valid time.
      resetPrimaryBookButtonSelectedSlot()
    }

    const primaryBookButtonSlotDurationMinutes = primaryBookButtonEffectiveSlot
      ?.slot?.end
      ? differenceInMinutes(
          new Date(primaryBookButtonEffectiveSlot.slot.end),
          new Date(primaryBookButtonEffectiveSlot.slot.start),
          { roundingMethod: 'round' }
        )
      : null

    useEffect(() => {
      if (showModal === true && providerSlotsCount > 0) {
        const insuranceName = query.insuranceId
          ? sessionStorage.getItem(sessionStorageKeys.INSURANCE_PROVIDER_NAME)
          : 'Self Pay'

        callRudderEventForBookingModalAllAvailabilityShown(
          providerDetails,
          userSelectedFilters,
          providerSlotsCount,
          insuranceName,
          isPrimaryBookButtonMobileEnabled
            ? {
                surface: PRIMARY_BOOK_BUTTON_TRACKING.SURFACE,
                device: PRIMARY_BOOK_BUTTON_DEVICE.MOBILE,
                variant: PRIMARY_BOOK_BUTTON_TRACKING.VARIANT_LABEL,
              }
            : undefined
        )
      }
    }, [showModal, providerSlotsCount, isPrimaryBookButtonMobileEnabled])

    // Portal straight to <body>: BaseModal's open/close transform makes it a
    // new containing block for `fixed` descendants, so a `fixed` footer
    // nested inside it can't track the real dynamic viewport (mobile browser
    // toolbar / safe area). Rendering it outside that subtree lets `fixed`
    // behave normally.
    // z-[1100]: above .BaseModal's z-index: 1050 (Modal.scss), so the footer
    // paints above the modal now that it's outside its DOM tree.
    const mobilePrimaryButtonV2 =
      isPrimaryBookButtonMobileEnabled && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="~fixed ~inset-x-0 ~bottom-0 ~z-[1100] ~flex ~h-24 ~w-full ~items-center ~bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFF_23.56%)] ~p-6"
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
            >
              <PrimaryBookButton
                slot={primaryBookButtonEffectiveSlot?.slot}
                format={format}
                timeZone={timeZone}
                onBook={handlePrimaryBookButtonConfirm}
                isSlotSelected={isPrimaryBookButtonSlotSelected}
                isLoading={isPrimaryBookButtonConfirming}
              />
            </div>,
            document.body
          )
        : null

    return (
      <PageLoader
        isLoading={slotsLoading}
        Components={{
          Loading: <Spinner />,
        }}
      >
        <div ref={ref} className={styles.ModalBody_subContainer}>
          {isPrimaryBookButtonMobileEnabled &&
            !!primaryBookButtonSlotDurationMinutes && (
              <p className="~px-6 ~mb-6 ~font-gibson ~text-base ~font-normal ~text-charcoal">
                This will be a {primaryBookButtonSlotDurationMinutes}-minute
                intake consultation.
              </p>
            )}
          {isSupportOrThirdPartyUser &&
            (isProfileMobile ? (
              // Profile mobile: styled V3 (react-select) trigger only, via the `isBoxed` prop.
              <div className="~px-6 ~mb-12">
                <p className="~mb-2 ~font-gibson ~text-base ~font-normal ~text-charcoal">
                  Patients will be booked in this timezone
                </p>
                <TimeZonePicker isBoxed showClientMsgHint={false} />
              </div>
            ) : (
              <TimeZonePicker />
            ))}
          <ModalAvailabilityFilterPills
            visitTypeOptions={visitTypeOptions}
            visitTypeSelectedIds={visitTypeSelectedIds}
            setVisitTypeSelectedIds={handleVisitTypeSelectedIdsChange}
            availabilityLabels={availabilityLabels}
            providerFirstName={providerDetails.firstName}
            timeOfDay={timeOfDay}
            setTimeOfDay={setTimeOfDay}
            timeOfDayFilters={timeOfDayFilters}
            dayOfWeek={dayOfWeek}
            setDayOfWeek={setDayOfWeek}
            dayOfWeekFilters={dayOfWeekFilters}
            idPrefix="view-avail-v1"
            className={isProfileMobile ? '~mb-12 max-[629px]:~mx-6' : undefined}
            isProfileMobile={isProfileMobile} // Profile mobile: horizontal-scroll pills + design copy typography.
            isEnabled={!isPrimaryBookButtonConfirming}
          />
          <SlotCard
            userSelectedAvailType={appointmentMode}
            providerDetails={providerDetails}
            dates={dates}
            showSlotSelection={isPrimaryBookButtonMobileEnabled}
            selectedSlot={primaryBookButtonEffectiveSlot}
            generatedSlots={generatedDatedSlots}
            onSlotClick={
              isPrimaryBookButtonMobileEnabled
                ? (slot) => {
                    trackSlotSelected(slot, 'normal')
                    handlePrimaryBookButtonSlotClick(slot)
                  }
                : (slot) => {
                    trackSlotSelected(slot, 'normal')
                    bookAppointment(slot)
                  }
            }
            isProfileMobile={isProfileMobile}
          />
        </div>
        {mobilePrimaryButtonV2}
      </PageLoader>
    )
  }
)

export default SlotGridBody
